import { TIME_FORMAT } from 'common/constants/timeFormat'
import { formatInSiteZone } from 'common/services/dateService'
import dayjs from 'dayjs'
import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'
import { type AppointmentCategories } from 'features/Schedule/api/schedule.types'
import { type AppointmentColorType } from 'features/SettingsOld/interfaces/ISettings'
import { type StatusColorName } from 'features/SettingsOld/state/api/types'

export const formatDroppedScheduleTime = (date: Date, timeZone?: string): string =>
  formatInSiteZone(date, timeZone, TIME_FORMAT.TIME_PICKER_HH_SS)

export const formatDroppedScheduleDate = (date: Date, timeZone?: string): string =>
  formatInSiteZone(date, timeZone, 'YYYY-MM-DD')

export const getAppointmentColorInputFromEvent = (
  data: Pick<IPatientSchedule, 'id' | 'status' | 'category'>,
  schedules: IPatientSchedule[],
): { statusCode?: number; categoryCode?: number } => {
  const fromSchedules = schedules.find((s) => s.id === data.id || Number(s.id) === Number(data.id))
  return {
    statusCode: data.status?.code ?? fromSchedules?.status?.code,
    categoryCode: data.category?.code ?? fromSchedules?.category?.code,
  }
}

export const getAppointmentColor = ({
  appointmentColor,
  categoryStatuses,
  statusColorName,
  statusCode,
  categoryCode,
}: {
  appointmentColor: AppointmentColorType
  categoryStatuses: AppointmentCategories[]
  statusColorName: StatusColorName[]
  statusCode?: number
  categoryCode?: number
}) => {
  const status = Array.isArray(statusColorName)
    ? statusColorName.find((s) => s.code === statusCode || Number(s.code) === Number(statusCode))
    : undefined
  const category = Array.isArray(categoryStatuses)
    ? categoryStatuses.find(
        (c) => c.code === categoryCode || Number(c.code) === Number(categoryCode),
      )
    : undefined

  if (appointmentColor === 'status') {
    return {
      color: status?.color,
      background: status?.background,
      borderColor: category?.color,
    }
  }
  return { color: category?.color, background: category?.background, borderColor: status?.color }
}

type ScheduleUpdateOverrides = {
  room: string | null | undefined
  start_time: string
  end_time: string
  consultation_staff?: number
}

export const buildScheduleUpdatePayload = (
  event: IPatientSchedule,
  { room, start_time, end_time, consultation_staff }: ScheduleUpdateOverrides,
) => ({
  date: event.date,
  room,
  start_time,
  end_time,
  temperature_bypass: event.temperature_bypass,
  consultation_staff: consultation_staff ?? event.consultation_staff,
  procedure_code: !event.procedure?.isCustom ? event.procedure?.code : null,
  procedure_description: event.procedure?.description,
  category_type_code: event.category?.code,
  title: event.title,
  comments: event.comments,
  punctuality_type_code: Number(event.punctuality?.appointment?.code || 0),
  punctuality_minutes: event.punctuality?.appointment?.minutes || 0,
  status_type_code: event.status?.code,
  patient: {
    id: event.patient?.id,
    first_name: event.patient?.first_name,
    last_name: event.patient?.last_name,
    phone: event.patient?.phone,
    language_type_code: event.patient?.language_type_code,
  },
})

type DropPointArgs = {
  target?: Element
  event?: unknown
}

const WORK_CELL_SELECTOR = 'td.e-work-cells, td.e-all-day-cells'

const isAppointmentChrome = (el: Element): boolean =>
  !!el.closest('.e-appointment, .e-schedule-event-clone, .e-drag-clone, .e-clone-element')

const getDragClientPoint = ({ event }: DropPointArgs): { x: number; y: number } | null => {
  const raw = ((event as { event?: Event })?.event ?? event) as
    | (MouseEvent & Partial<TouchEvent>)
    | undefined
  const touch = raw?.changedTouches?.[0]
  const x = touch?.clientX ?? raw?.clientX
  const y = touch?.clientY ?? raw?.clientY
  if (typeof x !== 'number' || typeof y !== 'number') return null
  return { x, y }
}

export const getDropPointElement = ({ target, event }: DropPointArgs): Element | null => {
  if (target instanceof Element) return target
  const point = getDragClientPoint({ event })
  if (!point) return null
  return document.elementFromPoint(point.x, point.y)
}

/**
 * The work cell under the cursor. After a timezone `refreshEvents(true)`, the dragged
 * appointment (or its clone) is often the event target — walking up from that lands in
 * the *source* column, so room changes get dropped and only time is saved.
 */
export const getDropWorkCell = (args: DropPointArgs): HTMLTableCellElement | null => {
  const point = getDragClientPoint(args)
  if (point && typeof document.elementsFromPoint === 'function') {
    const hit = document
      .elementsFromPoint(point.x, point.y)
      .find(
        (el) =>
          el instanceof Element && !isAppointmentChrome(el) && el.closest(WORK_CELL_SELECTOR),
      )
    const cell = hit?.closest(WORK_CELL_SELECTOR)
    if (cell instanceof HTMLTableCellElement) return cell
  }

  const fallback = args.target instanceof Element ? args.target : getDropPointElement(args)
  if (!fallback || isAppointmentChrome(fallback)) return null
  const cell = fallback.closest(WORK_CELL_SELECTOR)
  return cell instanceof HTMLTableCellElement ? cell : null
}

export const getDropGroupIndex = (args: DropPointArgs): number | null => {
  const cell = getDropWorkCell(args)
  if (!cell) return null
  const groupIndex = Number(cell.getAttribute('data-group-index'))
  return Number.isFinite(groupIndex) ? groupIndex : null
}

export const resolveScheduleRoomId = (roomId: unknown): string => {
  if (Array.isArray(roomId)) return String(roomId[0] ?? '')
  if (roomId == null) return ''
  return String(roomId)
}

type DragGroupSchedule = {
  dragAndDropModule?: { actionObj?: { groupIndex?: number } }
} | null

const toGroupIndex = (value: unknown): number | null => {
  const index = Number(value)
  return Number.isFinite(index) ? index : null
}

/**
 * Destination column for a drop. Syncfusion updates `actionObj.groupIndex` while dragging;
 * `dragStop` does not copy it onto the event args, and the DOM target is often the
 * appointment itself after timezone `refreshEvents(true)`.
 */
export const resolveDragGroupIndex = (
  args: DropPointArgs & { groupIndex?: number },
  schedule?: DragGroupSchedule,
  originalGroupIndex?: number | null,
): number | null => {
  const fromDrag = toGroupIndex(schedule?.dragAndDropModule?.actionObj?.groupIndex)
  const fromArgs = toGroupIndex(args.groupIndex)
  const fromCell = getDropGroupIndex(args)

  const differsFromOriginal = (index: number | null) =>
    index != null && (originalGroupIndex == null || index !== originalGroupIndex)

  if (differsFromOriginal(fromDrag)) return fromDrag
  if (differsFromOriginal(fromArgs)) return fromArgs
  if (differsFromOriginal(fromCell)) return fromCell
  return fromDrag ?? fromArgs ?? fromCell
}

export const snapToFiveMinutes = (date: Date, timeZone?: string): Date => {
  const zoned = timeZone ? dayjs(date).tz(timeZone) : dayjs(date)
  const remainder = zoned.minute() % 5

  if (remainder === 0) {
    return new Date(date.getTime())
  }

  const adjustment = remainder < 3 ? -remainder : 5 - remainder
  return zoned.add(adjustment, 'minute').second(0).millisecond(0).toDate()
}

type AvailableSlot = { start_time: string; end_time: string }

const mergeAvailableSlots = (slots: AvailableSlot[]): AvailableSlot[] => {
  if (!slots.length) return []

  const sorted = [...slots]
    .map((slot) => ({ ...slot }))
    .sort((a, b) => a.start_time.localeCompare(b.start_time))
  const merged = [sorted[0]]

  for (let i = 1; i < sorted.length; i++) {
    const previous = merged[merged.length - 1]
    if (previous.end_time >= sorted[i].start_time) {
      previous.end_time =
        previous.end_time > sorted[i].end_time ? previous.end_time : sorted[i].end_time
    } else {
      merged.push({ ...sorted[i] })
    }
  }

  return merged
}

export const isWithinAvailableSlots = ({
  dateKey,
  slots,
  start,
  end,
  timeZone,
}: {
  dateKey: string
  slots: AvailableSlot[]
  start: Date
  end: Date
  timeZone?: string
}): boolean => {
  const mergedSlots = mergeAvailableSlots(slots)
  if (!mergedSlots.length) return false

  const toWallClock = (date: Date) => formatInSiteZone(date, timeZone, 'HH:mm:ss')

  const normalizedStart = new Date(`${dateKey} ${toWallClock(start)}`)
  const normalizedEnd = new Date(`${dateKey} ${toWallClock(end)}`)

  return mergedSlots.some((slot) => {
    const slotStart = new Date(`${dateKey} ${slot.start_time}`)
    const slotEnd = new Date(`${dateKey} ${slot.end_time}`)
    return normalizedStart >= slotStart && normalizedEnd <= slotEnd
  })
}

export const SCHEDULE_COLUMN_WIDTH_PX = {
  base: 170,
  stdSmUp: 280,
  minUnit: 100,
} as const

/** Width scale vs default zoom (15-min). Zoom out → narrower; zoom in → wider. */
export const SCHEDULE_ZOOM_WIDTH_SCALE: Record<number, number> = {
  30: 0.7,
  15: 1,
  10: 1.2,
  5: 1.4,
  1: 1.6,
}

export const getBaseScheduleColumnWidth = (): number =>
  typeof window !== 'undefined' && window.matchMedia('(min-width: 576px)').matches
    ? SCHEDULE_COLUMN_WIDTH_PX.stdSmUp
    : SCHEDULE_COLUMN_WIDTH_PX.base

export const getZoomWidthScale = (interval: number): number => {
  const exact = SCHEDULE_ZOOM_WIDTH_SCALE[interval]
  if (exact != null) return exact

  const known = Object.keys(SCHEDULE_ZOOM_WIDTH_SCALE).map(Number)
  let closest = known[0]
  let closestDistance = Math.abs(closest - interval)
  for (let i = 1; i < known.length; i++) {
    const distance = Math.abs(known[i] - interval)
    if (distance < closestDistance) {
      closestDistance = distance
      closest = known[i]
    }
  }
  return SCHEDULE_ZOOM_WIDTH_SCALE[closest]
}

export const getZoomScaledBaseWidth = (
  interval: number,
  baseWidthPx: number = getBaseScheduleColumnWidth(),
): number => Math.round(baseWidthPx * getZoomWidthScale(interval))

export const clampScheduleColumnUnitWidth = (widthPx: number): number =>
  Math.max(SCHEDULE_COLUMN_WIDTH_PX.minUnit, Math.round(widthPx))

export const toReferenceColumnWidth = (renderedUnitWidthPx: number, interval: number): number => {
  const scale = getZoomWidthScale(interval)
  return clampScheduleColumnUnitWidth(renderedUnitWidthPx / scale)
}

type TimeInterval = { start: number; end: number }

export const getMaxConcurrentIntervals = (intervals: TimeInterval[]): number => {
  if (intervals.length === 0) return 1

  const points: Array<{ time: number; delta: number }> = []
  for (const { start, end } of intervals) {
    if (end <= start) continue
    points.push({ time: start, delta: 1 })
    points.push({ time: end, delta: -1 })
  }

  if (points.length === 0) return 1

  points.sort((a, b) => a.time - b.time || a.delta - b.delta)

  let current = 0
  let max = 0
  for (const point of points) {
    current += point.delta
    max = Math.max(max, current)
  }

  return Math.max(1, max)
}

export const getMaxConcurrentByColumnKey = (
  events: Array<Pick<IPatientSchedule, 'room_id' | 'start_time' | 'end_time'>>,
): Map<string, number> => {
  const intervalsByColumn = new Map<string, TimeInterval[]>()

  for (const event of events) {
    const key = event.room_id
    if (!key) continue

    const start = new Date(event.start_time).getTime()
    const end = new Date(event.end_time).getTime()
    if (Number.isNaN(start) || Number.isNaN(end)) continue

    const list = intervalsByColumn.get(key)
    if (list) {
      list.push({ start, end })
    } else {
      intervalsByColumn.set(key, [{ start, end }])
    }
  }

  const result = new Map<string, number>()
  for (const [key, intervals] of intervalsByColumn) {
    result.set(key, getMaxConcurrentIntervals(intervals))
  }
  return result
}

type ApplyColumnWidthsArgs = {
  scheduleElement: HTMLElement
  columnKeys: string[]
  maxConcurrentByColumn: Map<string, number>
  interval?: number
  referenceWidthByColumn?: Readonly<Record<string, number>>
  baseWidthPx?: number
  fillContainer?: boolean
  /** Waiting-room (or similar) column that absorbs leftover viewport width. */
  flexColumnKey?: string
  /** When false, overlapping appointments share a normal column instead of widening it. */
  scaleColumnsByOverlap?: boolean
}

const setVerticalViewColWidths = (
  scheduleElement: HTMLElement,
  widths: string[],
  options?: { fillContainer?: boolean },
): void => {
  const headerTable = scheduleElement.querySelector<HTMLElement>(
    '.e-vertical-view .e-date-header-wrap table',
  )
  const contentTable = scheduleElement.querySelector<HTMLElement>(
    '.e-vertical-view .e-content-wrap table',
  )
  const headerCols = scheduleElement.querySelectorAll<HTMLTableColElement>(
    '.e-vertical-view .e-date-header-wrap table col',
  )
  const contentCols = scheduleElement.querySelectorAll<HTMLTableColElement>(
    '.e-vertical-view .e-content-wrap table col',
  )

  if (options?.fillContainer && widths.length === 1) {
    const width = '100%'
    if (headerCols[0]) headerCols[0].style.width = width
    if (contentCols[0]) contentCols[0].style.width = width
    if (headerTable) {
      headerTable.style.tableLayout = 'fixed'
      headerTable.style.width = '100%'
      headerTable.style.minWidth = '100%'
    }
    if (contentTable) {
      contentTable.style.tableLayout = 'fixed'
      contentTable.style.width = '100%'
      contentTable.style.minWidth = '100%'
    }
    return
  }

  let totalPx = 0
  widths.forEach((width, index) => {
    if (headerCols[index]) headerCols[index].style.width = width
    if (contentCols[index]) contentCols[index].style.width = width
    totalPx += Number.parseFloat(width) || 0
  })

  if (totalPx > 0) {
    const total = `${Math.round(totalPx)}px`
    const tableWidth = options?.fillContainer ? '100%' : total
    const tableMinWidth = options?.fillContainer ? '100%' : total
    if (headerTable) {
      headerTable.style.tableLayout = 'fixed'
      headerTable.style.width = tableWidth
      headerTable.style.minWidth = tableMinWidth
    }
    if (contentTable) {
      contentTable.style.tableLayout = 'fixed'
      contentTable.style.width = tableWidth
      contentTable.style.minWidth = tableMinWidth
    }
  }
}

export const getScheduleColumnsViewportWidth = (scheduleElement: HTMLElement): number => {
  const wrap = scheduleElement.querySelector<HTMLElement>('.e-vertical-view .e-content-wrap')
  return wrap?.clientWidth ?? 0
}

/**
 * Gives leftover viewport width to one column (waiting room) so the grid fills
 * the calendar. Other columns keep their natural / user-resized widths.
 * The caller skips expansion when any visible column has a stored resize.
 */
export const expandFlexColumnWidths = ({
  widthsPx,
  flexIndex,
  containerWidthPx,
}: {
  widthsPx: number[]
  flexIndex: number
  containerWidthPx: number
}): number[] => {
  if (flexIndex < 0 || flexIndex >= widthsPx.length) return widthsPx
  if (!(containerWidthPx > 0) || widthsPx.length === 0) return widthsPx

  const others = widthsPx.reduce(
    (sum, width, index) => (index === flexIndex ? sum : sum + width),
    0,
  )
  const remaining = Math.floor(containerWidthPx - others)
  if (remaining <= widthsPx[flexIndex]) return widthsPx

  const next = [...widthsPx]
  next[flexIndex] = remaining
  return next
}

export const hasNonFlexColumnResize = ({
  columnKeys,
  flexColumnKey,
  referenceWidthByColumn = {},
}: {
  columnKeys: string[]
  flexColumnKey?: string
  referenceWidthByColumn?: Readonly<Record<string, number>>
}): boolean => {
  if (!flexColumnKey) return false
  return columnKeys.some((key) => key !== flexColumnKey && referenceWidthByColumn[key] != null)
}

export const computeColumnWidths = (multipliers: number[], baseWidthPx: number): string[] =>
  multipliers.map((multiplier) => `${Math.max(1, Math.floor(multiplier) || 1) * baseWidthPx}px`)

export const resolveColumnUnitWidths = ({
  columnKeys,
  interval = 15,
  referenceWidthByColumn = {},
  baseWidthPx = getBaseScheduleColumnWidth(),
}: {
  columnKeys: string[]
  interval?: number
  referenceWidthByColumn?: Readonly<Record<string, number>>
  baseWidthPx?: number
}): number[] => {
  const scale = getZoomWidthScale(interval)
  return columnKeys.map((key) => {
    const reference = referenceWidthByColumn[key] ?? baseWidthPx
    return clampScheduleColumnUnitWidth(reference * scale)
  })
}

export const applyScheduleColumnWidths = ({
  scheduleElement,
  columnKeys,
  maxConcurrentByColumn,
  interval = 15,
  referenceWidthByColumn = {},
  baseWidthPx = getBaseScheduleColumnWidth(),
  fillContainer = false,
  flexColumnKey,
  scaleColumnsByOverlap = true,
}: ApplyColumnWidthsArgs): void => {
  if (columnKeys.length === 0) return

  if (fillContainer && columnKeys.length === 1) {
    setVerticalViewColWidths(scheduleElement, ['100%'], { fillContainer: true })
    return
  }

  const unitWidths = resolveColumnUnitWidths({
    columnKeys,
    interval,
    referenceWidthByColumn,
    baseWidthPx,
  })
  const widthsPx = columnKeys.map((key, index) => {
    const concurrent = Math.max(1, Math.floor(maxConcurrentByColumn.get(key) ?? 1) || 1)
    const multiplier = scaleColumnsByOverlap ? concurrent : 1
    return unitWidths[index] * multiplier
  })

  const flexIndex = flexColumnKey ? columnKeys.indexOf(flexColumnKey) : -1
  const canExpandFlex =
    flexIndex >= 0 &&
    referenceWidthByColumn[columnKeys[flexIndex]] == null &&
    !hasNonFlexColumnResize({ columnKeys, flexColumnKey, referenceWidthByColumn })
  const containerWidthPx = canExpandFlex ? getScheduleColumnsViewportWidth(scheduleElement) : 0
  const nextWidthsPx = canExpandFlex
    ? expandFlexColumnWidths({
        widthsPx,
        flexIndex,
        containerWidthPx,
      })
    : widthsPx
  const totalPx = nextWidthsPx.reduce((sum, width) => sum + width, 0)
  const filledToViewport =
    canExpandFlex && containerWidthPx > 0 && Math.abs(totalPx - containerWidthPx) <= 1

  setVerticalViewColWidths(
    scheduleElement,
    nextWidthsPx.map((width) => `${width}px`),
    { fillContainer: filledToViewport },
  )
}

type LayoutBox = { top: number; height: number }
type LayoutResult = { left: number; width: number }

const boxesOverlap = (
  a: { top: number; bottom: number },
  b: { top: number; bottom: number },
): boolean => a.top < b.bottom && b.top < a.bottom

export const computeAppointmentLayout = (boxes: LayoutBox[]): LayoutResult[] => {
  const results: LayoutResult[] = boxes.map(() => ({ left: 0, width: 100 }))

  const items = boxes
    .map((box, index) => ({ index, top: box.top, bottom: box.top + box.height }))
    .sort((a, b) => a.top - b.top || b.bottom - a.bottom || a.index - b.index)

  let group: typeof items = []
  let groupEnd = Number.NEGATIVE_INFINITY

  const flushGroup = () => {
    if (group.length === 0) return

    const lanes: Array<typeof group> = []
    const laneByIndex = new Map<number, number>()

    for (const event of group) {
      let placedLane = lanes.findIndex((lane) => event.top >= lane[lane.length - 1].bottom)
      if (placedLane === -1) {
        lanes.push([event])
        placedLane = lanes.length - 1
      } else {
        lanes[placedLane].push(event)
      }
      laneByIndex.set(event.index, placedLane)
    }

    const laneCount = lanes.length
    for (const event of group) {
      const lane = laneByIndex.get(event.index) as number
      let colSpan = 1
      for (let next = lane + 1; next < laneCount; next++) {
        const conflict = lanes[next].some((other) => boxesOverlap(event, other))
        if (conflict) break
        colSpan++
      }
      results[event.index] = {
        left: (lane / laneCount) * 100,
        width: (colSpan / laneCount) * 100,
      }
    }

    group = []
    groupEnd = Number.NEGATIVE_INFINITY
  }

  for (const event of items) {
    if (group.length > 0 && event.top >= groupEnd) {
      flushGroup()
    }
    group.push(event)
    groupEnd = Math.max(groupEnd, event.bottom)
  }
  flushGroup()

  return results
}

/**
 * Re-lays out appointment elements in every vertical-view column so they fill the column
 * width (no right-edge gutter, no gaps between lanes), overriding Syncfusion's default
 * sizing. Reads each appointment's `top`/`height` px and sets `left`/`width` in %.
 * `leftGutterPx` insets each card so a corner initials badge stays inside the column.
 */
export const APPOINTMENT_INITIALS_GUTTER_PX = 16
export const APPOINTMENT_INITIALS_HANG_PX = 12
export const APPOINTMENT_AT_HEADER_CLASS = 'schedule-event--at-header'

export const applyAppointmentFill = (
  scheduleElement: HTMLElement,
  options?: { leftGutterPx?: number },
): void => {
  const leftGutterPx = options?.leftGutterPx ?? 0
  const wrappers = scheduleElement.querySelectorAll<HTMLElement>(
    '.e-vertical-view .e-appointment-wrapper',
  )

  wrappers.forEach((wrapper) => {
    const elements = Array.from(
      wrapper.querySelectorAll<HTMLElement>('.e-appointment:not(.e-schedule-event-clone)'),
    )
    if (elements.length === 0) return

    const boxes = elements.map((element) => ({
      top: Number.parseFloat(element.style.top || '0') || 0,
      height: Number.parseFloat(element.style.height || '0') || 0,
    }))

    const layout = computeAppointmentLayout(boxes)
    layout.forEach((position, index) => {
      const left =
        leftGutterPx > 0
          ? `calc(${position.left}% + ${leftGutterPx}px)`
          : `${position.left}%`
      const width =
        leftGutterPx > 0
          ? `calc(${position.width}% - ${leftGutterPx}px)`
          : `${position.width}%`
      elements[index].style.left = left
      elements[index].style.width = width
      elements[index].classList.toggle(
        APPOINTMENT_AT_HEADER_CLASS,
        boxes[index].top < APPOINTMENT_INITIALS_HANG_PX,
      )
    })
  })
}
